/**
 * Data layer for `goal-progress-widget`. Typed fetch wrappers over
 * `/api/v1/goal-progress-widget`. Raw `fetch()` is confined to this file
 * per the dash contract. Tenant header is pinned to `t1` (MVP convention).
 */

export interface GoalProgressWidgetRow {
	id: string;
	tenant_id: string;
	name?: string;
	goal_type?: string;
	progress?: number; // 0-100
	current?: number;
	target?: number;
	status?: "on_track" | "at_risk" | "behind" | "completed" | string;
	eta?: string | null;
	completed_at?: number | null;
	created_at: number;
	updated_at: number;
	[key: string]: unknown;
}

export interface GoalProgressSummary {
	active_goals: number;
	avg_progress: number;
	at_risk: number;
	completed_this_month: number;
}

export interface ListGoalProgressWidgetResponse {
	count: number;
	items: GoalProgressWidgetRow[];
	summary?: GoalProgressSummary;
}

export interface ListGoalProgressWidgetFilters {
	goal_type?: string;
}

const BASE = "/api/v1/goal-progress-widget";
const TENANT = "t1";

async function parseOrThrow<T>(res: Response): Promise<T> {
	const text = await res.text();
	let data: unknown = null;
	if (text) {
		try {
			data = JSON.parse(text);
		} catch {
			data = text;
		}
	}
	if (!res.ok) {
		const msg =
			data &&
			typeof data === "object" &&
			data !== null &&
			"error" in data &&
			typeof (data as { error: unknown }).error === "string"
				? (data as { error: string }).error
				: `HTTP ${res.status}`;
		throw new Error(msg);
	}
	return data as T;
}

function buildQuery(filters?: ListGoalProgressWidgetFilters): string {
	if (!filters) return "";
	const params = new URLSearchParams();
	if (filters.goal_type && filters.goal_type !== "all") {
		params.set("goal_type", filters.goal_type);
	}
	const s = params.toString();
	return s ? `?${s}` : "";
}

export async function list(
	filters?: ListGoalProgressWidgetFilters,
): Promise<ListGoalProgressWidgetResponse> {
	const res = await fetch(`${BASE}${buildQuery(filters)}`, {
		method: "GET",
		credentials: "include",
		headers: {
			accept: "application/json",
			"x-tenant-id": TENANT,
		},
	});
	return parseOrThrow<ListGoalProgressWidgetResponse>(res);
}

export const goalProgressWidgetQueryKeys = {
	all: ["goal-progress-widget"] as const,
	list: (filters?: ListGoalProgressWidgetFilters) =>
		[
			"goal-progress-widget",
			"list",
			filters?.goal_type ?? "all",
		] as const,
	detail: (id: string) => ["goal-progress-widget", id] as const,
};
